import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import {
  getStatus,
  getStatusLabel,
  StatusIndicatorIcon,
} from '@/components/staffer/AssignmentsBasedGradesTable/Table/Rows/StatusIndicator';
import { toDisplayScore } from '@/components/staffer/AssignmentsBasedGradesTable/Table/utils';
import useGradesTableStore from '../store/useGradesTableStore';
import { getSubmissionScore, StudentRowDataType } from '../utils';
import useQuestionAnswers from '../hooks/useQuestionAnswers';
import CommentButton from './CommentButton';
import TableData from './TableData';
import CommentDrawer from './CommentDrawer';

type TableRowProps = {
  rowData: StudentRowDataType;
};

const TableRow = ({ rowData }: TableRowProps) => {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const lowScoresSettings = useGradesTableStore(state => state.lowScoresSettings);
  const showPercentages = useGradesTableStore(state => state.showPercentages);
  const dueDate = moment(rowData.due_datetime);
  const submittedAt = moment(rowData.submitted_at);

  const score = getSubmissionScore(rowData);
  const assignmentComments = useQuestionAnswers({ assignmentId: rowData.assignment_id });

  const displayScore = useMemo(() => {
    if (typeof score !== 'number' || Number.isNaN(score)) return '';

    const displayedScore = toDisplayScore(score, rowData.max_points, showPercentages);
    return showPercentages ? String(displayedScore) : `${displayedScore}/${rowData.max_points}`;
  }, [score, rowData.max_points, showPercentages]);

  const status = getStatus({
    assignment: { max_points: rowData.max_points },
    isAssigned: true,
    lowScoresSettings,
    submission: rowData.submission_id ? {
      id: rowData.submission_id,
      in_progress: rowData.in_progress,
      submitted_at: rowData.submitted_at,
      total_score: rowData.total_score,
      total_score_override: rowData.total_score_override,
    } : undefined,
  });

  return (
    <>
      <tr
        key={rowData.assignment_id}
        className="tw-border-b tw-border-1 tw-border-med-grey tw-border-solid last:tw-border-b-0"
      >
        <TableData>
          <div className="tw-line-clamp-3 tw-leading-5">
            {!rowData.investigation && `${rowData.assignment_details.type}: `}{rowData.assignment_details.title}
          </div>
        </TableData>
        <TableData>
          {dueDate.format('MMM D, YYYY')} by {dueDate.format('h:mma')}
        </TableData>
        <TableData>
          {rowData.submitted_at
            ? `${submittedAt.format('MMM D, YYYY')} at ${submittedAt.format('h:mma')}`
            : 'N/A'}
        </TableData>
        <TableData>
          <div className="tw-flex tw-items-center tw-h-full tw-w-full tw-gap-x-2">
            <StatusIndicatorIcon type={status} /> {getStatusLabel({ status })}
          </div>
        </TableData>
        <TableData>
          {displayScore}
        </TableData>
        <TableData>
          <div className="tw-flex tw-items-center tw-h-full tw-w-full">
            {assignmentComments?.comments?.length > 0 && (
              <CommentButton
                commentDrawer={drawerOpen}
                newCommentCount={assignmentComments.newCommentCount}
                setCommentDrawer={setDrawerOpen}
              />
            )}
          </div>
        </TableData>
      </tr>
      {assignmentComments?.comments
        && (
          <CommentDrawer
            assignmentId={rowData.assignment_id}
            comments={assignmentComments.comments}
            drawerOpen={drawerOpen}
            score={displayScore}
            setDrawerOpen={setDrawerOpen}
          />
        )}
    </>
  );
};

export default TableRow;
